import { useId } from "react";
import type { UpfrontIssue } from "@/interfaces/review";
import { formatMoney } from "@/lib/campaigns";

export interface UpfrontAmountFieldProps {
  /** The raw text the client typed — digits and one point. Empty = pay the minimum. */
  value: string;
  onChange: (next: string) => void;
  /** The organisation's minimum deposit for this campaign, in whole gifts. */
  minimum: number;
  /** The campaign budget — the most that can be paid upfront. */
  budget: number;
  perGift: number;
  /** The organisation's deposit percentage, for the helper line. */
  minUpfrontPc: number;
  /** The deposit the figures above are now using — the typed amount rounded up to whole gifts. */
  rounded: number;
  issue: UpfrontIssue | null;
}

/**
 * Step 4's "pay more upfront": a pay-as-you-give or Pay Later client may put down MORE than the
 * organisation's minimum deposit, never less, and never more than the campaign. The bounds are
 * stated inline so nobody has to discover them from a 422, and the rounding to whole gifts is shown
 * the moment it moves the figure, the way the deposit tooltip already explains it for the minimum.
 *
 * Empty means the minimum, which is exactly today's behaviour — the field changes nothing until a
 * figure is typed. An out-of-range figure turns the helper into the error and disables the CTA; the
 * server applies the same bounds (`chooseUpfront`), so this is a courtesy, not the gate.
 */
export default function UpfrontAmountField({
  value,
  onChange,
  minimum,
  budget,
  perGift,
  minUpfrontPc,
  rounded,
  issue,
}: UpfrontAmountFieldProps) {
  const id = useId();
  const typed = value.trim() !== "";
  const help =
    issue === "below_minimum"
      ? `Below the minimum deposit of ${formatMoney(minimum)} (${minUpfrontPc}%).`
      : issue === "above_budget"
        ? `More than the campaign budget of ${formatMoney(budget)}.`
        : issue === "not_a_number"
          ? "Enter an amount in dollars."
          : typed && rounded !== Number(value)
            ? `Rounded up to ${formatMoney(rounded)} — whole gifts of ${formatMoney(perGift)}. Minimum ${formatMoney(minimum)}, up to ${formatMoney(budget)}.`
            : `Minimum ${formatMoney(minimum)} (${minUpfrontPc}%), up to ${formatMoney(budget)}. Rounded up to whole gifts of ${formatMoney(perGift)}.`;

  return (
    <div
      className={`mt-2.5 mb-0.5 rounded-card border px-3.5 py-3 ${
        issue ? "border-good-wine bg-good-wine-bg" : "border-card-border bg-clear-dust"
      }`}
    >
      <div className="flex items-center justify-between font-lato text-[13px] font-semibold text-rodeo-black">
        <label htmlFor={id}>Pay more upfront</label>
        <span className="text-[11px] font-normal text-graphite">optional</span>
      </div>
      <div className="mt-2 flex items-center rounded-btn border border-smoke bg-white px-3 py-2.5 font-lato text-[15px]">
        <span className="mr-2 text-[12px] text-graphite">$</span>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          className="w-full min-w-0 border-0 bg-transparent font-semibold text-rodeo-black outline-none"
          placeholder={formatMoney(minimum).replace(/^\$/, "")}
          value={value}
          onChange={(e) =>
            onChange(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"))
          }
          aria-invalid={issue !== null}
          aria-describedby={`${id}-help`}
        />
        <span className="ml-2 shrink-0 text-[12px] text-graphite">deposit</span>
      </div>
      <p
        id={`${id}-help`}
        role={issue ? "alert" : undefined}
        className={`mt-1.5 mb-0 font-lato text-[12px] leading-[1.4] ${
          issue ? "font-semibold text-good-wine" : "text-graphite"
        }`}
      >
        {help}
      </p>
    </div>
  );
}
